import React, { Component } from 'react';
import { connect } from 'react-redux';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Toast } from 'primereact/toast';
import axios from '../../../axios-url';

import { REDIRECT_TO_URL, HASACCESS } from '../../../shared/utility';
import { DROPDOWN_CONFIG, ZATCA_UNIT_SUBMENU_ID } from '../../../shared/Config';
import Messages from '../../../shared/UI/Messages/Messages';
import FooterButton from '../../../shared/UI/FooterButton/FooterButton';
import * as actions from '../../../store/actions/index';
import {
    ZATCA_ENVIRONMENT_OPTIONS,
    ZATCA_INVOICE_TYPE_OPTIONS,
    zatcaUnitListFunction,
    validateZatcaUnit,
    apiErrorMessage
} from './zatcaShared';

const noteStyle = {
    padding: '0.75em 1em',
    borderRadius: '4px',
    background: '#fff4e5',
    border: '1px solid #f5c26b',
    color: '#6b4500',
    marginBottom: '1em'
};

/**
 * Adds, edits or shows one branch's ZATCA EGS unit.
 *
 * Everything that identifies the unit is written into its ZATCA certificate, so
 * those fields lock once onboarding has started. Only the active switch stays
 * editable after that. The serial number is generated by the backend, as ZATCA
 * requires, and never typed in.
 */
class AddZatcaUnit extends Component {
    state = {
        access: undefined,
        type: 'add',
        loading: false,
        touch: false,
        branchOptions: [],
        egsUnitId: null,
        branchId: null,
        commonName: '',
        organizationUnit: '',
        location: '',
        crNumber: '',
        invoiceTypeFlags: '1100',
        industry: 'Restaurant',
        environment: 'Sandbox',
        serialNumber: '',
        onboardingStatus: 'NotOnboarded',
        isActive: true
    }

    componentDidMount() {
        const { type, id } = this.props.match.params;
        this.setState({ access: HASACCESS(ZATCA_UNIT_SUBMENU_ID), type: type });
        this.loadBranches();
        if (type !== 'add') {
            this.loadUnit(id);
        }
    }

    loadBranches = () => {
        axios.get('/master/api/res/GetbranchList?companyId=' + this.props.loggedUserDetail.CompanyId)
            .then(response => {
                const rows = Array.isArray(response.data) ? response.data : [];
                const branchOptions = rows.map(branch => ({
                    label: (branch.branchCode ? branch.branchCode + ' - ' : '') + branch.branchName,
                    value: branch.branchId,
                    branchName: branch.branchName
                }));
                this.setState({ branchOptions });
            })
            .catch(err => {
                this.growl.show({ severity: 'error', summary: null, detail: apiErrorMessage(err, 'Could not load branches') });
            });
    }

    loadUnit = (id) => {
        this.setState({ loading: true });
        axios.get('/master/api/zatca/GetZatcaEgsUnit?id=' + id)
            .then(response => {
                const unit = response.data || {};
                this.setState({
                    loading: false,
                    touch: false,
                    egsUnitId: unit.egsUnitId,
                    branchId: unit.branchId,
                    commonName: unit.commonName || '',
                    organizationUnit: unit.organizationUnit || '',
                    location: unit.location || '',
                    crNumber: unit.crNumber || '',
                    invoiceTypeFlags: unit.invoiceTypeFlags || '1100',
                    industry: unit.industry || 'Restaurant',
                    environment: unit.environment || 'Sandbox',
                    serialNumber: unit.serialNumber || '',
                    onboardingStatus: unit.onboardingStatus || 'NotOnboarded',
                    isActive: unit.isActive !== false
                });
            })
            .catch(err => {
                this.setState({ loading: false });
                this.growl.show({ severity: 'error', summary: null, detail: apiErrorMessage(err, 'Could not load the ZATCA unit') });
            });
    }

    change = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value, touch: true });
    }

    changeDropdown = (name) => (e) => {
        this.setState({ [name]: e.value, touch: true });
    }

    changeBranch = (e) => {
        const branch = this.state.branchOptions.find(option => option.value === e.value);
        this.setState(state => ({
            branchId: e.value,
            // The branch name is a sensible starting point, and stays editable.
            organizationUnit: state.organizationUnit || (branch ? branch.branchName : ''),
            touch: true
        }));
    }

    save = () => {
        const errors = validateZatcaUnit(this.state);
        if (errors.length > 0) {
            errors.forEach(error => this.growl.show({ severity: 'error', summary: 'Validation Error', detail: error }));
            return;
        }

        const user = this.props.loggedUserDetail.userName ?? 'Admin';
        const data = {
            CompanyId: this.props.loggedUserDetail.CompanyId,
            BranchId: this.state.branchId,
            CommonName: this.state.commonName.trim(),
            OrganizationUnit: this.state.organizationUnit.trim(),
            Location: this.state.location.trim().toUpperCase(),
            CrNumber: this.state.crNumber.trim(),
            InvoiceTypeFlags: this.state.invoiceTypeFlags,
            Industry: this.state.industry.trim(),
            Environment: this.state.environment,
            IsActive: this.state.isActive
        };

        if (this.state.type === 'edit') {
            data.EgsUnitId = this.state.egsUnitId;
            data.UpdatedBy = user;
            data.UpdatedDate = new Date();
        } else {
            data.CreatedBy = user;
            data.CreatedDate = new Date();
        }

        this.props.processDynamicData('ZatcaEgsUnit', this.state.type, data, zatcaUnitListFunction(this.props.loggedUserDetail.CompanyId));
    }

    reset = () => {
        this.setState({
            touch: false,
            branchId: null,
            commonName: '',
            organizationUnit: '',
            location: '',
            crNumber: '',
            invoiceTypeFlags: '1100',
            industry: 'Restaurant',
            environment: 'Sandbox',
            isActive: true
        });
    }

    label = (text, required, htmlFor) => (
        <div className="p-col-12">
            <label htmlFor={htmlFor}>{text} {required && <span className="requiredColor">*</span>}</label>
        </div>
    )

    render() {
        if (this.props.success) {
            REDIRECT_TO_URL('/zatca-units', this.props);
        }

        const access = this.state.access;
        let addAccess = true;
        let editAccess = true;
        if (access !== undefined) {
            if (access.viewAccess === false) {
                this.props.history.push('/dashboard');
            }
            addAccess = access.addAccess === true;
            editAccess = access.editAccess === true;
        }

        const isView = this.state.type === 'view';
        const identityLocked = this.state.onboardingStatus !== 'NotOnboarded';
        const identityDisabled = isView || identityLocked;
        const title = { add: 'Add', edit: 'Edit', view: 'View' }[this.state.type] || '';

        return (
            <div className="p-grid">
                {(this.props.loading || this.state.loading) && <div className="page-loader"></div>}
                {this.props.error !== null && <Messages type="error" message={this.props.error} />}
                {this.props.success !== null && <Messages type="success" message={this.props.success} />}
                <Toast ref={(el) => this.growl = el} />
                <div className="p-col-12">
                    <div className="card card-w-title" style={{ position: 'relative' }}>
                        <h1>{title} ZATCA Unit</h1>

                        {identityLocked &&
                            <div style={noteStyle}>
                                This unit has started ZATCA onboarding, so its identity is in its certificate and can no
                                longer be changed. To change it, revoke the unit and set up a new one for the branch.
                            </div>
                        }

                        <div className="p-grid mt1">
                            <div className="p-col-12 p-md-6">
                                {this.label('Branch', true, 'zatca-unit-branch')}
                                <div className="p-col-12">
                                    <Dropdown inputId="zatca-unit-branch" className="p-col-12" placeholder="Select Branch"
                                        options={this.state.branchOptions} value={this.state.branchId}
                                        onChange={this.changeBranch} disabled={identityDisabled} {...DROPDOWN_CONFIG} />
                                </div>
                            </div>

                            <div className="p-col-12 p-md-6">
                                {this.label('Unit name', true, 'zatca-unit-commonName')}
                                <div className="p-col-12">
                                    <InputText id="zatca-unit-commonName" className="p-col-12" name="commonName" maxLength={100}
                                        value={this.state.commonName} onChange={this.change} disabled={identityDisabled}
                                        placeholder="e.g. ChendhurRMS-Riyadh" />
                                    <small>A name or asset number for this unit. It appears in the certificate.</small>
                                </div>
                            </div>

                            <div className="p-col-12 p-md-6">
                                {this.label('Organization unit', true, 'zatca-unit-organizationUnit')}
                                <div className="p-col-12">
                                    <InputText id="zatca-unit-organizationUnit" className="p-col-12" name="organizationUnit" maxLength={100}
                                        value={this.state.organizationUnit} onChange={this.change} disabled={identityDisabled} />
                                    <small>Usually the branch name.</small>
                                </div>
                            </div>

                            <div className="p-col-12 p-md-6">
                                {this.label('Location', true, 'zatca-unit-location')}
                                <div className="p-col-12">
                                    <InputText id="zatca-unit-location" className="p-col-12" name="location" maxLength={100}
                                        value={this.state.location} onChange={this.change} disabled={identityDisabled}
                                        placeholder="e.g. RRRD2929" />
                                    <small>The branch's short national address.</small>
                                </div>
                            </div>

                            <div className="p-col-12 p-md-6">
                                {this.label('CR number', true, 'zatca-unit-crNumber')}
                                <div className="p-col-12">
                                    <InputText id="zatca-unit-crNumber" className="p-col-12" name="crNumber" maxLength={20} keyfilter="pint"
                                        value={this.state.crNumber} onChange={this.change} disabled={identityDisabled} />
                                    <small>Commercial registration number for this branch.</small>
                                </div>
                            </div>

                            <div className="p-col-12 p-md-6">
                                {this.label('Invoice types', true, 'zatca-unit-invoiceTypeFlags')}
                                <div className="p-col-12">
                                    <Dropdown inputId="zatca-unit-invoiceTypeFlags" className="p-col-12"
                                        options={ZATCA_INVOICE_TYPE_OPTIONS} value={this.state.invoiceTypeFlags}
                                        onChange={this.changeDropdown('invoiceTypeFlags')} disabled={identityDisabled}
                                        {...DROPDOWN_CONFIG} filter={false} showClear={false} />
                                </div>
                            </div>

                            <div className="p-col-12 p-md-6">
                                {this.label('Industry', true, 'zatca-unit-industry')}
                                <div className="p-col-12">
                                    <InputText id="zatca-unit-industry" className="p-col-12" name="industry" maxLength={100}
                                        value={this.state.industry} onChange={this.change} disabled={identityDisabled} />
                                </div>
                            </div>

                            <div className="p-col-12 p-md-6">
                                {this.label('Environment', true, 'zatca-unit-environment')}
                                <div className="p-col-12">
                                    <Dropdown inputId="zatca-unit-environment" className="p-col-12"
                                        options={ZATCA_ENVIRONMENT_OPTIONS} value={this.state.environment}
                                        onChange={this.changeDropdown('environment')} disabled={identityDisabled}
                                        {...DROPDOWN_CONFIG} filter={false} showClear={false} />
                                    {this.state.environment === 'Production' &&
                                        <small style={{ color: '#c62828' }}>Invoices from a Production unit have fiscal effect.</small>
                                    }
                                </div>
                            </div>

                            <div className="p-col-12 p-md-6">
                                {this.label('EGS serial number', false, 'zatca-unit-serialNumber')}
                                <div className="p-col-12">
                                    <InputText id="zatca-unit-serialNumber" className="p-col-12" disabled={true}
                                        value={this.state.serialNumber || 'Generated when the unit is saved'} />
                                </div>
                            </div>

                            <div className="p-col-12 p-md-6">
                                {this.label('Active', false, 'zatca-unit-isActive')}
                                <div className="p-col-12">
                                    <InputSwitch inputId="zatca-unit-isActive" checked={this.state.isActive} disabled={isView}
                                        onChange={(e) => this.setState({ isActive: e.value, touch: true })} />
                                </div>
                            </div>
                        </div>

                        <div className="p-col-12 p-lg-12">
                            <FooterButton
                                access={{ addAccess, editAccess }}
                                disabled={!this.state.touch}
                                type={this.state.type.toLowerCase()}
                                save={this.save}
                                reset={this.reset}
                                to="/zatca-units"
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => {
    return {
        loggedUserDetail: state.auth.userObject,
        loading: state.master.loading,
        error: state.master.error,
        success: state.master.success
    }
}

const mapDispatchToProps = dispatch => {
    return {
        processDynamicData: (model, type, data, func) => dispatch(actions.processDynamicData(model, type, data, func))
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(AddZatcaUnit);
